import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ZoomIn, ZoomOut, Maximize2, Map as MapIcon, Video, Images, Box } from 'lucide-react';
import type { FloorplanViewMode, StoreProfileDetail, MediaLocation } from '../../types';
import { ComplianceBadge } from '../ComplianceBadge';
import { MediaAvailabilityBadge } from '../MediaAvailabilityBadge';
import { buildAisleMediaSummaries } from '../../lib/aisleMediaLinks';
import { cacheBustUrl } from '../../lib/cacheBustUrl';
import { FloorplanEditor } from '../FloorplanEditor';
import { FloorplanCanvas } from '../FloorplanCanvas';
import { FloorplanPhotoTagger } from '../FloorplanPhotoTagger';

interface FloorplanTabProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
  focusedAisleNumber?: number | null;
  onFocusAisle?: (aisleNumber: number | null) => void;
  onOpenDigitalTwin?: (aisleNumber: number) => void;
  onOpenWalkthroughs?: (aisleNumber: number) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const viewModeLabels: Record<FloorplanViewMode, string> = {
  layout: 'Store Layout',
  media_inventory: 'Media Inventory',
  live_campaigns: 'Live Campaigns',
};

export function FloorplanTab({
  profile,
  storeId,
  storeIdParam,
  focusedAisleNumber,
  onFocusAisle,
  onOpenDigitalTwin,
  onOpenWalkthroughs,
  onDirtyChange,
}: FloorplanTabProps) {
  const [viewMode, setViewMode] = useState<FloorplanViewMode>('layout');
  const [zoom, setZoom] = useState(1);
  const [selectedAisleId, setSelectedAisleId] = useState<string | null>(null);
  const [selectedMediaId, setSelectedMediaId] = useState<string | null>(null);
  const [formatFilter, setFormatFilter] = useState<string>('all');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [isEditingFloorplan, setIsEditingFloorplan] = useState(false);

  const imageZoneTags = profile.floorplanImageZoneTags ?? {};
  const zonePhotoCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const image of profile.images) {
      const zoneId = image.floorplanZoneId ?? imageZoneTags[image.id];
      if (!zoneId) continue;
      counts[zoneId] = (counts[zoneId] ?? 0) + 1;
    }
    return counts;
  }, [profile.images, imageZoneTags]);

  const aisleSummaries = useMemo(() => buildAisleMediaSummaries(profile), [profile]);
  const summaryByAisleId = useMemo(
    () => new Map(aisleSummaries.map((summary) => [summary.aisle.id, summary])),
    [aisleSummaries],
  );

  useEffect(() => {
    onDirtyChange?.(isEditingFloorplan);
  }, [isEditingFloorplan, onDirtyChange]);

  useEffect(() => {
    if (focusedAisleNumber == null) return;
    const aisle = profile.aisles.find((item) => item.number === focusedAisleNumber);
    if (aisle) {
      setSelectedAisleId(aisle.id);
      setSelectedMediaId(null);
    }
  }, [focusedAisleNumber, profile.aisles]);

  const selectedAisle = profile.aisles.find((a) => a.id === selectedAisleId);
  const selectedSummary = selectedAisle ? summaryByAisleId.get(selectedAisle.id) : undefined;
  const selectedMedia = profile.mediaLocations.find((m) => m.id === selectedMediaId);
  const aisleCampaigns = selectedAisle
    ? profile.campaigns.filter((c) => c.aisleId === selectedAisle.id && c.phase === 'current')
    : [];

  const filteredMedia = profile.mediaLocations.filter((m) => {
    if (formatFilter === 'all') return true;
    return m.format === formatFilter;
  });

  const handleMarkerClick = (media: MediaLocation) => {
    setSelectedMediaId(media.id);
    if (media.aisleId) {
      setSelectedAisleId(media.aisleId);
      const aisle = profile.aisles.find((item) => item.id === media.aisleId);
      if (aisle) onFocusAisle?.(aisle.number);
    }
  };

  const handleAisleClick = (aisleId: string) => {
    setSelectedAisleId(aisleId);
    setSelectedMediaId(null);
    setSelectedZoneId(null);
    const aisle = profile.aisles.find((item) => item.id === aisleId);
    if (aisle) onFocusAisle?.(aisle.number);
  };

  const floorplanDisplayUrl = cacheBustUrl(profile.floorplanImageUrl, profile.floorplanImageVersion);

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-white p-4 overflow-auto' : ''}`}>
      {storeId != null && storeIdParam != null && (
        <div className="mb-4">
          <FloorplanEditor
            storeId={storeId}
            storeIdParam={storeIdParam}
            retailerId={profile.retailerId}
            aisles={profile.aisles}
            zones={profile.floorplanZones}
            floorplanImageUrl={floorplanDisplayUrl}
            floorplanImageVersion={profile.floorplanImageVersion}
            retailerInstoreLocations={profile.retailerInstoreLocations}
            storeInstoreLocations={profile.storeInstoreLocations}
            campaignInstoreLocationIds={profile.campaigns
              .map((campaign) => campaign.instoreLocationId)
              .filter((id): id is number => id != null)}
            onEditingChange={setIsEditingFloorplan}
          />
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1">
              {(Object.keys(viewModeLabels) as FloorplanViewMode[]).map((mode) => (
                <Button
                  key={mode}
                  variant={viewMode === mode ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode(mode)}
                >
                  {viewModeLabels[mode]}
                </Button>
              ))}
            </div>
            <Select value={formatFilter} onValueChange={setFormatFilter}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Media format" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All formats</SelectItem>
                <SelectItem value="aisle_fin">Aisle Fin</SelectItem>
                <SelectItem value="shelf_talker">Shelf Talker</SelectItem>
                <SelectItem value="floor_graphic">Floor Graphic</SelectItem>
                <SelectItem value="fridge_vinyl">Fridge Vinyl</SelectItem>
                <SelectItem value="entrance_media">Entrance Media</SelectItem>
                <SelectItem value="promotional_bay">Promotional Bay</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-1 ml-auto">
              <Button variant="outline" size="icon" onClick={() => setZoom((z) => Math.min(z + 0.2, 2))}><ZoomIn className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" onClick={() => setZoom((z) => Math.max(z - 0.2, 0.6))}><ZoomOut className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" onClick={() => setIsFullscreen(!isFullscreen)}><Maximize2 className="h-4 w-4" /></Button>
            </div>
          </div>

          {!isEditingFloorplan && (
            <Card>
              <CardContent className="p-4 overflow-auto">
                <FloorplanCanvas
                  zones={profile.floorplanZones}
                  aisles={viewMode === 'layout' ? profile.aisles : []}
                  imageUrl={floorplanDisplayUrl}
                  selectedZoneId={selectedZoneId}
                  selectedAisleId={selectedAisleId}
                  onZoneClick={(zoneId) => {
                    setSelectedZoneId(zoneId);
                    setSelectedAisleId(null);
                  }}
                  onAisleClick={handleAisleClick}
                  mediaLocations={filteredMedia}
                  showMedia={viewMode !== 'layout'}
                  zoom={zoom}
                />
              </CardContent>
            </Card>
          )}

          <div className="flex flex-wrap gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-500" /> Walk-through</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500" /> Install images</span>
            <span className="flex items-center gap-1"><MapIcon className="h-3 w-3" /> Click zone to tag photos · aisle for digital twin</span>
          </div>

          {storeId != null && storeIdParam != null && (
            <FloorplanPhotoTagger
              storeId={storeId}
              storeIdParam={storeIdParam}
              images={profile.images}
              zones={profile.floorplanZones}
              imageZoneTags={imageZoneTags}
              selectedZoneId={selectedZoneId}
              onSelectedZoneChange={setSelectedZoneId}
            />
          )}
        </div>

        <Card className="w-full lg:w-80 shrink-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Location Details</CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedAisle && !selectedMedia && !selectedZoneId && (
              <p className="text-sm text-gray-500">Select a zone or aisle on the floorplan to view details.</p>
            )}

            {selectedZoneId && !selectedAisle && (
              <div className="space-y-3">
                {(() => {
                  const zone = profile.floorplanZones.find((item) => item.id === selectedZoneId);
                  if (!zone) return null;
                  const photoCount = zonePhotoCounts[zone.id] ?? 0;
                  return (
                    <>
                      <div>
                        <p className="font-medium">{zone.label}</p>
                        <p className="text-sm text-gray-500 capitalize">{zone.type.replace(/_/g, ' ')}</p>
                        {zone.instoreLocationId != null && (
                          <p className="text-xs text-gray-500 mt-1">Posting location #{zone.instoreLocationId}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
                        <Images className="h-3 w-3 mr-1" />
                        {photoCount} tagged photo{photoCount === 1 ? '' : 's'}
                      </Badge>
                    </>
                  );
                })()}
              </div>
            )}

            {selectedAisle && (
              <div className="space-y-3">
                <div>
                  <p className="font-medium">Aisle {selectedAisle.number} — {selectedAisle.name}</p>
                  <p className="text-sm text-gray-500">{selectedAisle.category} · {selectedAisle.lengthMetres}m</p>
                </div>

                {selectedSummary && (
                  <div className="flex flex-wrap gap-2">
                    {selectedSummary.hasWalkthrough && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
                        <Video className="h-3 w-3 mr-1" />
                        {selectedSummary.walkthroughs.length} walk-through{selectedSummary.walkthroughs.length === 1 ? '' : 's'}
                      </Badge>
                    )}
                    {selectedSummary.hasInstallImages && (
                      <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200">
                        <Images className="h-3 w-3 mr-1" />
                        {selectedSummary.installImages.length} install image{selectedSummary.installImages.length === 1 ? '' : 's'}
                      </Badge>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  {onOpenDigitalTwin && (
                    <Button
                      type="button"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => onOpenDigitalTwin(selectedAisle.number)}
                    >
                      <Box className="h-4 w-4 mr-2" />
                      Open Digital Twin
                    </Button>
                  )}
                  {onOpenWalkthroughs && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => onOpenWalkthroughs(selectedAisle.number)}
                    >
                      <Video className="h-4 w-4 mr-2" />
                      View walk-through{selectedSummary && selectedSummary.walkthroughs.length !== 1 ? 's' : ''}
                    </Button>
                  )}
                </div>

                {aisleCampaigns.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Current campaigns</p>
                    {aisleCampaigns.map((c) => (
                      <div key={c.id} className="border rounded p-2 text-sm">
                        <p className="font-medium">{c.campaignName}</p>
                        <p className="text-gray-500">{c.formatLabel} · {c.installedQuantity}/{c.plannedQuantity}</p>
                        <ComplianceBadge status={c.complianceStatus} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No current campaigns in this aisle.</p>
                )}
              </div>
            )}

            {selectedMedia && (
              <div className="space-y-3 mt-4 pt-4 border-t">
                <div>
                  <p className="font-medium">{selectedMedia.formatLabel}</p>
                  <p className="text-sm text-gray-500">{selectedMedia.locationLabel}</p>
                </div>
                <MediaAvailabilityBadge availability={selectedMedia.availability} />
                <div className="text-sm space-y-1">
                  <p><span className="text-gray-500">Zone:</span> {selectedMedia.zone}</p>
                  <p><span className="text-gray-500">Dimensions:</span> {selectedMedia.dimensions}</p>
                  <p><span className="text-gray-500">Condition:</span> {selectedMedia.condition}</p>
                  {selectedMedia.currentCampaignName && (
                    <p><span className="text-gray-500">Campaign:</span> {selectedMedia.currentCampaignName}</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
